/**
 * useTourProgress
 *
 * Persists guided tour progress to localStorage and syncs to the DB on an
 * interval defined by the global progress configuration.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TourProgressConfig, TourProgressState } from '@/config/guided-tours/types';

interface UseTourProgressOptions {
  moduleKey: string;
  userId?: string;
  progressConfig?: TourProgressConfig | null;
  enabled?: boolean;
}

export interface UseTourProgressResult {
  progress: TourProgressState;
  isFormatPreferred: (format: string) => boolean;
  setPreferredFormat: (format: string) => void;
  markStepVerified: (stepId: string) => void;
  markFlowActive: (flowId: string, stepIndex: number, objectiveId?: string | null) => void;
  markFlowCompleted: (flowId: string) => void;
  resetProgress: () => void;
  loading: boolean;
}

const DEFAULT_STORAGE_PREFIX = 'ta_tour_progress';

function getStorageKey(prefix: string, moduleKey: string, userId?: string): string {
  const userSegment = userId ? `:${userId}` : '';
  return `${prefix}:${moduleKey}${userSegment}`;
}

function buildDefaultProgress(): TourProgressState {
  return {
    verified_step_ids: [],
    verified_step_timestamps: {},
    completed_flow_ids: [],
    last_active_flow_id: null,
    last_active_objective_id: null,
    last_active_step_index: null,
  };
}

function readProgressFromStorage(
  storageKey: string,
  storageMode: 'local' | 'db' | 'hybrid'
): TourProgressState {
  if (storageMode === 'db' || typeof window === 'undefined') {
    return buildDefaultProgress();
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return buildDefaultProgress();
    }

    const parsed = JSON.parse(raw) as Partial<TourProgressState>;
    return {
      ...buildDefaultProgress(),
      ...parsed,
    };
  } catch {
    return buildDefaultProgress();
  }
}

function writeProgressToStorage(
  storageKey: string,
  progress: TourProgressState,
  storageMode: 'local' | 'db' | 'hybrid'
): void {
  if (storageMode === 'db' || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch {
    // Storage may be full or unavailable.
  }
}

export function useTourProgress({
  moduleKey,
  userId,
  progressConfig,
  enabled = true,
}: UseTourProgressOptions): UseTourProgressResult {
  const storageMode = progressConfig?.storage ?? 'hybrid';
  const storagePrefix = progressConfig?.storage_prefix ?? DEFAULT_STORAGE_PREFIX;
  const syncIntervalMs = progressConfig?.sync_interval_ms ?? 30000;
  const storageKey = getStorageKey(storagePrefix, moduleKey, userId);

  // Tour-video capture sessions set this flag so every run starts with clean
  // progress — otherwise previously verified step ids auto-advance the runner
  // and the recording skips steps.
  const captureMode =
    typeof window !== 'undefined' &&
    window.localStorage.getItem('ta_tour_capture_mode') === '1';

  const [progress, setProgress] = useState<TourProgressState>(() =>
    captureMode
      ? buildDefaultProgress()
      : readProgressFromStorage(storageKey, storageMode)
  );
  const [loading, setLoading] = useState(false);
  const pendingSyncRef = useRef(false);

  // Load persisted DB state once on mount.
  useEffect(() => {
    if (captureMode || !enabled || storageMode === 'local' || !moduleKey) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/v1/guided-tours/progress/${moduleKey}`);
        if (!response.ok) {
          throw new Error(`Failed to load tour progress: ${response.statusText}`);
        }

        const data = await response.json();
        if (!cancelled && data?.success && data.progress) {
          const merged = {
            ...buildDefaultProgress(),
            ...data.progress,
          };
          setProgress(merged);
          writeProgressToStorage(storageKey, merged, storageMode);
        }
      } catch (err) {
        console.warn('[useTourProgress] Failed to load progress:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [enabled, moduleKey, storageKey, storageMode, captureMode]);

  // Sync to DB on an interval when in hybrid/db mode.
  useEffect(() => {
    if (captureMode || !enabled || storageMode === 'local' || !moduleKey) {
      return;
    }

    const sync = async () => {
      if (!pendingSyncRef.current) {
        return;
      }

      try {
        const response = await fetch(`/api/v1/guided-tours/progress/${moduleKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ progress }),
        });

        if (response.ok) {
          pendingSyncRef.current = false;
        }
      } catch (err) {
        console.warn('[useTourProgress] Failed to sync progress:', err);
      }
    };

    const intervalId = window.setInterval(sync, syncIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [captureMode, enabled, moduleKey, progress, storageMode, syncIntervalMs]);

  const persist = useCallback(
    (next: TourProgressState) => {
      setProgress(next);
      if (!captureMode) {
        writeProgressToStorage(storageKey, next, storageMode);
        pendingSyncRef.current = true;
      }
    },
    [storageKey, storageMode, captureMode]
  );

  const markStepVerified = useCallback(
    (stepId: string) => {
      const timestamp = new Date().toISOString();
      setProgress((current) => {
        if (current.verified_step_ids.includes(stepId)) {
          return current;
        }

        const next: TourProgressState = {
          ...current,
          verified_step_ids: [...current.verified_step_ids, stepId],
          verified_step_timestamps: {
            ...current.verified_step_timestamps,
            [stepId]: timestamp,
          },
        };

        writeProgressToStorage(storageKey, next, storageMode);
        pendingSyncRef.current = true;
        return next;
      });
    },
    [storageKey, storageMode]
  );

  const markFlowActive = useCallback(
    (flowId: string, stepIndex: number, objectiveId?: string | null) => {
      setProgress((current) => {
        const next: TourProgressState = {
          ...current,
          last_active_flow_id: flowId,
          last_active_objective_id: objectiveId ?? null,
          last_active_step_index: stepIndex,
        };

        writeProgressToStorage(storageKey, next, storageMode);
        pendingSyncRef.current = true;
        return next;
      });
    },
    [storageKey, storageMode]
  );

  const markFlowCompleted = useCallback(
    (flowId: string) => {
      setProgress((current) => {
        if (!flowId || (current.completed_flow_ids ?? []).includes(flowId)) {
          return current;
        }

        const next: TourProgressState = {
          ...current,
          completed_flow_ids: [...(current.completed_flow_ids ?? []), flowId],
        };

        writeProgressToStorage(storageKey, next, storageMode);
        pendingSyncRef.current = true;
        return next;
      });
    },
    [storageKey, storageMode]
  );

  const setPreferredFormat = useCallback(
    (format: string) => {
      const storageKeyFormat = `${storageKey}:preferred_format`;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKeyFormat, format);
        } catch {
          // ignore
        }
      }
    },
    [storageKey]
  );

  const isFormatPreferred = useCallback(
    (format: string): boolean => {
      const storageKeyFormat = `${storageKey}:preferred_format`;
      if (typeof window === 'undefined') {
        return false;
      }
      try {
        return window.localStorage.getItem(storageKeyFormat) === format;
      } catch {
        return false;
      }
    },
    [storageKey]
  );

  const resetProgress = useCallback(() => {
    const next = buildDefaultProgress();
    persist(next);
  }, [persist]);

  return {
    progress,
    isFormatPreferred,
    setPreferredFormat,
    markStepVerified,
    markFlowActive,
    markFlowCompleted,
    resetProgress,
    loading,
  };
}
